import { useTranslation } from 'react-i18next'

import type { ScreenOnStage } from '../types'
import { CandidatePhoto } from './shared'

/**
 * Le profil de la personne qui défile, en grand.
 *
 * La salle voit quelqu'un marcher sur le podium et se demande qui c'est : la
 * télé répond — photo, numéro (celui du bulletin), nom, le tour en cours, et
 * ce qu'elle a dit d'elle-même. Rien d'autre : ni nom civil, ni contact.
 */
export function RunwayCard({ onStage }: { onStage: ScreenOnStage }) {
  const { t } = useTranslation()
  const { candidate, round } = onStage
  const facts = [
    candidate.age !== null ? t('screen.age', { count: candidate.age }) : null,
    candidate.activity,
  ].filter(Boolean)

  return (
    <article className="grid h-full min-h-0 grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] overflow-hidden rounded-xl border-2 border-[color-mix(in_srgb,var(--champagne-400)_60%,transparent)] bg-bg-elevated">
      <div className="relative min-h-0">
        <CandidatePhoto url={candidate.photo_url} number={candidate.number} name={candidate.name} />
        {candidate.number !== null ? (
          <span className="absolute bottom-[1.5vh] left-[1.5vh] rounded-lg bg-black/75 px-[1.6vh] py-[0.6vh] font-mono text-[min(7vh,4vw)] font-semibold leading-none text-champagne-300">
            {t('screen.number', { number: candidate.number })}
          </span>
        ) : null}
      </div>

      <div className="flex min-h-0 min-w-0 flex-col justify-center gap-[2.2vh] overflow-hidden px-[3.5vh] py-[3vh]">
        <div>
          <p className="text-[min(2.8vh,1.7vw)] uppercase tracking-[0.18em] text-ember-300">
            {t('screen.runwayOverline')}
            {candidate.contest ? ` · ${candidate.contest}` : ''}
          </p>
          <p className="mt-[0.8vh] font-display text-[min(4.4vh,2.6vw)] leading-tight text-champagne-300">
            {round.name}
          </p>
        </div>

        <h2 className="break-words font-display text-[min(9vh,5vw)] leading-[1.02] text-content-primary">
          {candidate.name}
        </h2>

        {facts.length > 0 ? (
          <p className="text-[min(3.6vh,2.1vw)] leading-snug text-content-primary">
            {facts.join(' · ')}
          </p>
        ) : null}
        {candidate.education_level ? (
          <p className="text-[min(3vh,1.8vw)] leading-snug text-content-secondary">
            {t('screen.education', { level: candidate.education_level })}
          </p>
        ) : null}

        {candidate.motto ? (
          <blockquote className="border-l-4 border-champagne-400 pl-[2vh] font-display text-[min(3.8vh,2.2vw)] italic leading-snug text-content-primary">
            {t('screen.motto', { motto: candidate.motto })}
          </blockquote>
        ) : null}

        {candidate.three_words.length > 0 ? (
          <div>
            <p className="text-[min(2.4vh,1.5vw)] uppercase tracking-[0.14em] text-content-muted">
              {t('screen.threeWords')}
            </p>
            <ul className="mt-[1vh] flex flex-wrap gap-[1vh]">
              {candidate.three_words.map((word) => (
                <li
                  key={word}
                  className="rounded-full border border-[color-mix(in_srgb,var(--champagne-400)_60%,transparent)] px-[1.8vh] py-[0.5vh] text-[min(3.2vh,1.9vw)] text-champagne-300"
                >
                  {word}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </article>
  )
}
